/* ============================================================================
 * Sales Report ⇄ Net Profit Analyzer — the Fixed Expense tab's plumbing (brief §9, §10).
 *
 *   fixedShareQuery()   the selected period as the same query /reports/expense-details takes;
 *                       the server resolves it with the sales card's own window, clamped to today
 *   saveReturnPoint()   remembers the period, the open Expense Details window and the tab, so Back
 *                       from the Analyzer lands exactly where the person started
 *   takeReturnPoint()   read once on the Sales Report's mount
 * ========================================================================== */

const fmt = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

/** Same priority order as fetchExpenseDetails in SalesReport.js. */
export function fixedShareQuery({ selectedWeek, selectedDate, selectedPeriod }) {
  const p = new URLSearchParams();
  if (selectedWeek && selectedWeek.isWeekSelection) {
    p.set('weekStart', fmt(selectedWeek.start));
    p.set('weekEnd', fmt(selectedWeek.end));
  } else if (selectedWeek && selectedWeek.isCustomRange) {
    if (selectedWeek.isSingleDay) p.set('date', fmt(selectedWeek.start));
    else { p.set('weekStart', fmt(selectedWeek.start)); p.set('weekEnd', fmt(selectedWeek.end)); }
  } else if (selectedDate && selectedDate.isMonthSelection) {
    p.set('period', 'month');
    p.set('monthYear', `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}`);
  } else if (selectedDate) {
    p.set('date', fmt(selectedDate));
  } else {
    const map = { Today: 'today', 'This Week': 'week', 'This Month': 'month', 'This Year': 'year' };
    if (map[selectedPeriod]) p.set('period', map[selectedPeriod]);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

const KEY = 'salesReport:returnPoint';

const packDate = (d) => (d instanceof Date ? { __date: d.toISOString() } : d);
const unpackDate = (d) => (d && d.__date ? new Date(d.__date) : d);

export function saveReturnPoint({ selectedPeriod, selectedDate, selectedWeek, displayText, tab = 'fixed', openExpense = true }) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      kind: 'state',
      selectedPeriod,
      selectedDate: selectedDate ? packDate(selectedDate) : null,
      selectedWeek: selectedWeek ? { ...selectedWeek, start: packDate(new Date(selectedWeek.start)), end: packDate(new Date(selectedWeek.end)) } : null,
      displayText,
      tab,
      openExpense,
    }));
  } catch (_) { /* private mode: Back still returns to the report, on its default period */ }
}

export function clearReturnPoint() {
  try { sessionStorage.removeItem(KEY); } catch (_) { /* nothing to clear */ }
}

/**
 * The saved point, or null. Read without clearing — it is read from a state initialiser, which
 * React may call twice — and cleared by clearReturnPoint() in a mount effect.
 */
export function peekReturnPoint() {
  let raw = null;
  try { raw = sessionStorage.getItem(KEY); } catch (_) { return null; }
  if (!raw) return null;
  try {
    const r = JSON.parse(raw);
    if (r.kind === 'month') {
      const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return {
        selectedPeriod: '', selectedWeek: null, openExpense: !!r.openExpense, tab: r.tab || 'stockin',
        selectedDate: { month: r.month, year: r.year, isMonthSelection: true }, displayText: `${names[r.month]} ${r.year}`,
      };
    }
    return {
      selectedPeriod: r.selectedPeriod || '',
      selectedDate: r.selectedDate ? unpackDate(r.selectedDate) : null,
      selectedWeek: r.selectedWeek ? { ...r.selectedWeek, start: unpackDate(r.selectedWeek.start), end: unpackDate(r.selectedWeek.end) } : null,
      displayText: r.displayText || r.selectedPeriod || 'Today',
      tab: r.tab || 'fixed',
      openExpense: r.openExpense !== false,
    };
  } catch (_) {
    return null;
  }
}

/** "incl. ₹X fixed" — the Expense card's quiet line (brief §9.4). */
export function fixedCardLine(share) {
  if (!share || !share.available) return null;
  const total = Number(share.total) || 0;
  const hasSalaryCards = (share.groups || []).some((g) => g.key === 'salaries');
  if (total <= 0 && !(share.excludesSalaries && hasSalaryCards)) return null;
  const amt = `₹${Math.round(total).toLocaleString('en-IN')}`;
  return `incl. ${amt} fixed${share.estimated ? ' (est.)' : ''}${share.excludesSalaries && hasSalaryCards ? ' · excl. staff salaries' : ''}`;
}
